import {computed, Injectable, OnDestroy, signal} from '@angular/core';
import {
  ArticleCart,
  Cart,
  CartSsCheckoutResponse,
  CheckoutData,
  convertCartToSsCheckout,
  DinerMode,
  PaymentMethod,
  SsCheckoutRespDiscountInfo
} from "./cart.types";
import {BaseService} from "../../core/services/base.service";
import {catchError, of, Subject, take, throwError, timeout, TimeoutError} from "rxjs";
import {HttpClient} from "@angular/common/http";
import {AppConfigService} from "../../core/config/config.service";
import {SS_CHECKOUT_ENDPOINT, SsResult, SsResultStatusCode} from "../stockstore-pos/stockstore-pos.types";
import {TranslocoService} from "@jsverse/transloco";
import cloneDeep from 'lodash-es/cloneDeep';
import {FidelityData} from "../fidelity/fidelity.types";
import {v7 as uuidv7} from 'uuid';
import {CouponsService} from "../coupons/coupons.service";

@Injectable({
  providedIn: 'root'
})
export class CartService extends BaseService implements OnDestroy {

  private _unsubscribeAll: Subject<any> = new Subject<any>();

  config = AppConfigService.Config;
  ssLocalEndpoint = computed<string | undefined>(() => this.config()?.endpoints?.kioskPos);

  constructor(private httpClient: HttpClient,
              private transloco: TranslocoService,
              private couponsService: CouponsService) {
    super();
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  cart = signal<Cart>({
    items: [],
    dinerMode: 'restaurant',
    paymentMethod: 'cash',
    orderId: this.generateOrderId(),
    activeCoupons: []
  });

  // Store discount information from SS response
  cartEntriesDiscounts = signal<SsCheckoutRespDiscountInfo[]>([]);
  cartDiscounts = signal<SsCheckoutRespDiscountInfo[]>([]);


  isCustomerLoggedIn = computed<boolean>(() => this.cart().fidelityData != null);

  itemsCount = computed(() => {
    return this.cart().items.reduce((a, b) => a + b.quantity, 0);
  });

  /**
   * Generates a unique order ID
   */
  private generateOrderId(): string {
    return `KIOSK-${uuidv7()}`.toUpperCase();
  }

  /**
   * Subtotal of the current cart, calculated using base prices.
   */
  subBaseTotal = computed(() => {
    let subtotal = 0;
    for (const article of this.cart().items) {
      subtotal += ArticleCart.baseTotalPrice(article);
    }

    return subtotal;
  });

  /**
   * Subtotal of the current cart calculated using prices. Used in the main page to display prices in a more customer friendly way.
   *
   */
  subTotal = computed(() => {
    let subtotal = 0;
    for (const article of this.cart().items) {
      subtotal += ArticleCart.totalPrice(article);
    }

    return subtotal;
  });


  /**
   * Sum of all discounts applied to the cart, including discounts applied to single entries, to the total cart and any special or promotional price
   *
   */
  totalDiscounts = computed(() => {

    //We have to consider, for each article, the difference between the basePrice and the final price. In this way, we will consider special prices and discounts.
    const entriesFinalPrices = this.cart().items.reduce((acc, article) => acc + article.finalPriceTotal(this.getDiscountsForCartEntry(article)), 0);
    const entriesBasePrices = this.cart().items.reduce((acc, article) => acc + ArticleCart.baseTotalPrice(article), 0);

    const entriesDiscounts = Math.abs(entriesBasePrices - entriesFinalPrices);

    const cartDiscount = this.cartDiscounts().reduce((acc, discount) => acc + discount.amount, 0) / 100.0;

    //Do not display a discount sum higher than the subtotal.
    return Math.min(entriesDiscounts + cartDiscount, this.subBaseTotal());
  });

  /**
   * Final charge for the current cart. Considers any discount.
   */
  total = computed(() => {
    return Math.max(this.subBaseTotal() - this.totalDiscounts(), 0);
  });

  setDinerMode(mode: DinerMode): void {
    this.cart.update(a => ({...a, dinerMode: mode}));
  }

  setPaymentMethod(method: PaymentMethod): void {
    this.cart.update(a => ({...a, paymentMethod: method}));
  }

  setOrderStartTimestamp(timestamp?: string): void {
    this.cart.update(a => ({...a, orderStartTimestamp: timestamp}));
  }

  setFidelityData(data?: FidelityData): void {
    this.cart.update(a => ({...a, fidelityData: data}));
  }

  setCheckoutAdditionalData(data?: CheckoutData): void {
    this.cart.update(a => ({...a, checkoutAdditionalData: data}));
  }

  addArticleToCart(newArticle: ArticleCart) {

    this.cart.update(a => {
      const newCart = {...a};
      newCart.items.push(cloneDeep(newArticle));
      return newCart;
    })

  }

  clear() {
    // Release all active coupons before clearing the cart
    const activeCoupons = this.cart().activeCoupons;
    if (activeCoupons.length > 0) {
      this.couponsService.releaseCoupon({
        orderId: this.cart().orderId,
        couponCodes: activeCoupons
      })
        .pipe(take(1))
        .subscribe({
          error: (error) => {
            // Log error but don't block the clear operation
            console.warn('Failed to release coupons:', error);
          }
        });
    }

    this.cart.set({
      items: [],
      dinerMode: "restaurant",
      paymentMethod: "cash",
      orderId: this.generateOrderId(),
      activeCoupons: []
    });
    //Clear promotions
    this.cartEntriesDiscounts.set([]);
    this.cartDiscounts.set([]);

  }

  updateQuantity(index: number, increment: boolean) {
    const item = this.cart().items.at(index);
    if (item == undefined)
      return;

    if (increment)
      item.quantity += 1;
    else if (item.quantity > 1)
      item.quantity -= 1;

    this.cart.update(a => {
      return {...a};
    })

  }

  removeArticle(specifiedItem: ArticleCart) {

    // Remove the specified article and all related promotional items
    const itemsToRemove = [specifiedItem, ...this.findAssociatedArticles(specifiedItem)];

    // Check if the specified item is a promotional article from coupon (not upsell)
    // Only the specifiedItem can have a coupon, not the associated items
    let couponToRelease: string | undefined;
    if (specifiedItem.isCouponPromotional && specifiedItem.couponPromo?.couponCode) {
      couponToRelease = specifiedItem.couponPromo.couponCode;
    }

    // Remove items from cart
    this.cart().items = this.cart().items.filter(item => !itemsToRemove.includes(item));

    // Remove coupon from active coupons list if it's a coupon promotional article
    if (couponToRelease) {
      // Use removeCoupon which will handle the release automatically
      this.removeCoupon(couponToRelease);
    } else {
      // Just update cart if no coupon to release (e.g., upsell articles)
      this.cart.update(a => {
        return {...a};
      });
    }
  }

  updateArticleToCart(articleCart: ArticleCart, index: number) {
    this.cart().items[index] = cloneDeep(articleCart);

    this.cart.update(a => {
      return {...a};
    })
  }

  /**
   * Adds a coupon to the active coupons list
   */
  addCoupon(couponCode: string): void {
    this.cart.update(cart => {
      if (!cart.activeCoupons.includes(couponCode)) {
        return {
          ...cart,
          activeCoupons: [...cart.activeCoupons, couponCode]
        };
      }
      return cart;
    });
  }

  /**
   * Removes a coupon from the active coupons list and releases it through the service
   */
  removeCoupon(couponCode: string): void {
    // Remove from active coupons first
    this.cart.update(cart => ({
      ...cart,
      activeCoupons: cart.activeCoupons.filter(c => c !== couponCode)
    }));

    // Release coupon through service
    this.couponsService.releaseCoupon({
      orderId: this.cart().orderId,
      couponCodes: [couponCode]
    })
      .pipe(take(1))
      .subscribe({
        error: (error) => {
          // Log error but don't block the operation
          console.warn('Failed to release coupon:', error);
        }
      });
  }

  /**
   * Calls the calculate API of SS.
   */
  checkoutToSS() {
    // If demo mode is enabled, return a mock successful response
    if (AppConfigService.isInDemoMode()) {

      const cartTotal = Math.round(this.total() * 100);

      const mockResponse: SsResult<CartSsCheckoutResponse> = {
        isSuccess: true,
        errorMessages: [],
        statusCode: SsResultStatusCode.OK,
        result: {
          cartTotal: cartTotal,
          totalDiscount: 0,
          subTotal: cartTotal
        }
      };
      return of(mockResponse);
    }

    const cart = this.cart();

    //Convert cart to dto for ss checkout
    const dto = convertCartToSsCheckout(cart, "calculate", this.config(), this.transloco.getActiveLang());

    //prod: this.ssLocalEndpoint() + SS_CHECKOUT_ENDPOINT
    //mock: "http://127.0.0.1:1221/" + SS_CHECKOUT_ENDPOINT
    return this.httpClient.post<SsResult<CartSsCheckoutResponse>>(this.ssLocalEndpoint() + SS_CHECKOUT_ENDPOINT, {
      ...dto
    })
      .pipe(
        timeout(300000), //30 sec
        catchError(error => {
          if (error instanceof TimeoutError) {
            error = {
              ...error,
              error: {
                statusCode: SsResultStatusCode.ERR_SS_TIMEOUT
              }
            };
          }
          return throwError(() => error);
        })
      );
  }

  /**
   * Updates the cart state from a passed calculate response.
   */
  updateStateFromCheckoutResponse(response: CartSsCheckoutResponse): void {
    this.cartEntriesDiscounts.set(response.cartEntriesDiscounts ?? []);
    this.cartDiscounts.set(response.cartDiscounts ?? []);

    if (response?.invalidatedCoupons) {
      for (let invalidCoupon of response.invalidatedCoupons) {
        // Remove the invalidated coupon from active coupons
        this.removeCoupon(invalidCoupon.couponCode);
      }
    }

  }


  /**
   * Returns a list of articles associated to the specified one. Eg: promotional upsells.
   * @param article
   */
  findAssociatedArticles(article: ArticleCart) {
    return this.cart().items.filter(a => a.upsell && a.upsell.parentCartEntryId === article.entryId);
  }

  /**
   * Returns every discount associated to a single cart entry.
   * @param article
   */
  getDiscountsForCartEntry(article: ArticleCart): SsCheckoutRespDiscountInfo[] {
    return this.cartEntriesDiscounts().filter(discount => discount.cartEntryId === article.entryId);
  }

}
